import React, { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

const NavLink = ({ href, label, testid, onClick }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={testid}
    className="ps-link text-sm tracking-[0.14em] uppercase text-zinc-200 hover:text-[var(--ps-gold)]"
  >
    {label}
  </a>
);

const Navbar = () => {
  const { t, lang, toggle } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, id) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/60 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => handleNav(e, "top")}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="relative w-14 h-14 flex items-center justify-center bg-[#f4ead7] border border-[var(--ps-gold)] group-hover:scale-105 transition-all duration-500 shadow-[0_0_22px_rgba(197,168,128,0.35)]">
            <img
              src="https://customer-assets.emergentagent.com/job_theatre-tech-hub/artifacts/7ojvjvzb_p_edited.png"
              alt="Proscenio Solutions logo (P + S)"
              className="w-14 h-14 object-contain mix-blend-multiply"
            />
          </span>
          <span className="font-serif-display text-lg md:text-xl tracking-wide">
            Proscenio <span className="text-[var(--ps-brand-red)]">Solutions</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink href="#about" label={t.nav.about} testid="nav-about" onClick={(e) => handleNav(e, "about")} />
          <NavLink href="#services" label={t.nav.services} testid="nav-services" onClick={(e) => handleNav(e, "services")} />
          <NavLink href="#brands" label={t.nav.brands} testid="nav-brands" onClick={(e) => handleNav(e, "brands")} />
          <NavLink href="#team" label={t.nav.team} testid="nav-team" onClick={(e) => handleNav(e, "team")} />
          <NavLink href="#contact" label={t.nav.contact} testid="nav-contact" onClick={(e) => handleNav(e, "contact")} />
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="language-switcher"
            aria-label="Toggle language"
            className="group flex items-center gap-1 text-xs tracking-[0.18em] border border-white/15 hover:border-[var(--ps-gold)] px-3 py-2 transition-colors duration-300"
          >
            <span
              className={lang === "it" ? "text-[var(--ps-gold)]" : "text-zinc-400"}
              data-testid="lang-it"
            >
              IT
            </span>
            <span className="text-zinc-600">/</span>
            <span
              className={lang === "en" ? "text-[var(--ps-gold)]" : "text-zinc-400"}
              data-testid="lang-en"
            >
              EN
            </span>
          </button>
          <button
            className="md:hidden border border-white/15 p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            <div className="w-5 h-[1px] bg-white mb-1" />
            <div className="w-5 h-[1px] bg-white mb-1" />
            <div className="w-5 h-[1px] bg-white" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-black/95 border-t border-white/5" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            <NavLink href="#about" label={t.nav.about} testid="nav-about-m" onClick={(e) => handleNav(e, "about")} />
            <NavLink href="#services" label={t.nav.services} testid="nav-services-m" onClick={(e) => handleNav(e, "services")} />
            <NavLink href="#brands" label={t.nav.brands} testid="nav-brands-m" onClick={(e) => handleNav(e, "brands")} />
            <NavLink href="#team" label={t.nav.team} testid="nav-team-m" onClick={(e) => handleNav(e, "team")} />
            <NavLink href="#contact" label={t.nav.contact} testid="nav-contact-m" onClick={(e) => handleNav(e, "contact")} />
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
